import { makeStyles } from '@material-ui/core/styles';
import { SecondaryColors, TextColors, TextOpacities } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const lightTheme = {
  root: {
    '& $title': {
      color: SecondaryColors.dark,
    },
    '& $content': {
      color: TextColors.dark,
    },
    '& $tabs': {
      '&:not(.MuiTabs-vertical):before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light3)}`,
      },
      '&.MuiTabs-vertical': {
        '&:before': {
          borderLeft: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light3)}`,
        },
        '& .MuiTabs-indicator': {
          backgroundColor: SecondaryColors.dark,
        },
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.dark, TextOpacities.light2),

        '&.Mui-selected, &:hover': {
          color: TextColors.dark,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.dark,
    },
  },
};

const darkTheme = {
  root: {
    '& $title': {
      color: SecondaryColors.main,
    },
    '& $content': {
      color: TextColors.light,
    },
    '& $tabs': {
      '&:not(.MuiTabs-vertical):before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.dark3)}`,
      },
      '&.MuiTabs-vertical': {
        '&:before': {
          borderLeft: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.dark3)}`,
        },
        '& .MuiTabs-indicator': {
          backgroundColor: SecondaryColors.main,
        },
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.light, TextOpacities.dark2),

        '&.Mui-selected, &:hover': {
          color: TextColors.light,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.main,
    },
  },
};

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.light-theme': lightTheme.root,
      '&.dark-theme': darkTheme.root,
    },
    title: {
      '&.MuiTypography-root': {
        marginBottom: '12px',

        [muiTheme.breakpoints.up('md')]: {
          marginBottom: '38px',
        },
      },
    },
    link: {
      '&:not(:first-child)': {
        marginTop: muiTheme.spacing(4),

        [muiTheme.breakpoints.down('sm')]: {
          width: '100%',
        },
      },
    },
    content: {
      [muiTheme.breakpoints.up('md')]: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignContent: 'center',
      },

      '& .tabs': {
        position: 'relative',

        [muiTheme.breakpoints.up('md')]: {
          flex: '1 1 40%',
          marginRight: '40px',
          order: 1,
        },
      },

      '& .tabPanels': {
        marginBottom: '30px',

        [muiTheme.breakpoints.up('md')]: {
          flex: '1 1 60%',
          marginLeft: '40px',
          marginBottom: '0',
          order: 2,
        },
      },
    },
    tabs: {
      position: 'relative',

      '&:not(.MuiTabs-vertical)': {
        display: 'inline-flex',
        padding: '2px 2px 0px 0px',
        margin: '-2px -2px 0px -32px',
        width: 'calc(100% + 64px)',

        '&:before': {
          content: '""',
          width: 'calc(100% - 64px)',
          position: 'absolute',
          top: 0,
          left: '32px',
          bottom: 0,
        },

        '& .MuiTabs-scrollButtons': {
          width: '32px',
        },
      },

      '&.MuiTabs-vertical': {
        padding: '2px 2px 2px 0',
        margin: '-2px -2px -2px 0',

        '&:before': {
          content: '""',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        },

        '& .MuiTabs-indicator': {
          height: '28px !important',
          left: 0,
          zIndex: 1,
        },

        '& .MuiTabs-flexContainer': {
          paddingLeft: '6px',
        },

        '& .MuiTabs-fixed': {
          padding: '2px 2px 2px 0',
          margin: '-2px -2px -2px 0',
        },
      },
    },
    tab: {
      maxWidth: '300px',

      [muiTheme.breakpoints.up('md')]: {
        '&.MuiTab-root': {
          flexDirection: 'column',
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          justifyContent: 'flex-start',
          textAlign: 'left',
          minHeight: '28px',
          maxWidth: '100%',
          paddingTop: '1.5px',
          paddingBottom: '1.5px',

          '& + *': {
            marginTop: '14px',
          },

          '& > *:last-child': {
            transition: 'opacity 150ms ease-in-out',
          },
        },
      },

      '& .MuiTypography-body1': {
        [muiTheme.breakpoints.down('sm')]: {
          lineHeight: '1em',
        },
      },

      '&:not([aria-selected="true"])': {
        height: '0',

        [muiTheme.breakpoints.up('md')]: {
          '& > *:last-child:not(:first-child)': {
            opacity: 0,
            visibility: 'hidden',
          },
        },
      },

      '&.MuiTab-textColorInherit': {
        opacity: 1,
      },
    },
    tabPanel: {
      '& > * + *': {
        marginTop: muiTheme.spacing(2),
      },
      paddingTop: '28px',
      animation: 'enter 150ms ease-out',
    },
    tabDescription: {
      ...muiTheme.typography.h5,

      [muiTheme.breakpoints.up('md')]: {
        marginTop: '16px',
        marginBottom: muiTheme.spacing(6),
      },

      '& p': {
        marginTop: 0,
        marginBottom: 0,

        '& + *': {
          marginTop: '16px',
        },
      },

      '& > *:last-child > a': {
        ...muiTheme.typography.button,
        display: 'block',
      },

      '& > *:last-child': {
        [muiTheme.breakpoints.up('md')]: {
          marginBottom: '12px',
        },
      },
    },
    detail: {
      '&:not(:last-child)': {
        marginBottom: muiTheme.spacing(2.5),
      },
    },
    media: {
      '&:not(:last-child)': {
        marginBottom: muiTheme.spacing(3),
      },
    },
    superheading: {},
  }),
  { name: 'GenCTLVerticalContent' },
);

export default useStyles;
